import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { downloadInvoicePdf, InvoiceDocument, InvoiceLine, mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { fmtDateShort, inr, inrExact } from '../design/atoms';

/* Membership → a sale's invoice: a plan, a pack, a product, a booking (every
   court or slot of it), a store cart, a desk sale. Every figure is the server's
   invoice document — the one its PDF is drawn from — so this page, the PDF, the
   pay page and the gym's records all say the same thing. Tax invoice: GSTIN,
   place of supply, HSN/SAC and GST per line, CGST/SGST or IGST. Bill of supply:
   none of that. Same content as the app's InvoiceView. */

const STATUS: Record<InvoiceDocument['status'], { label: string; cls: string }> = {
  paid: { label: 'Paid', cls: 'tag sage' },
  partial: { label: 'Part paid', cls: 'tag gold' },
  due: { label: 'Due', cls: 'tag clay' },
  cancelled: { label: 'Cancelled', cls: 'tag' },
};

/** Whole rupees stay whole on the hero; the breakdown is to the paisa, as on the PDF. */
const hero = (n: number) => (Number.isInteger(n) ? inr(n) : inrExact(n));

export default function MemberInvoicePage() {
  const { purchaseId } = useParams<{ purchaseId: string }>();
  const { token } = useMemberAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState<InvoiceDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    if (!token || !purchaseId) return;
    setError(null);
    mfetch<{ data: InvoiceDocument }>(token, `/member/invoices/${purchaseId}`)
      .then(r => setInv(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load this invoice'));
  }, [token, purchaseId]);
  useEffect(load, [load]);

  const download = async () => {
    if (!token || !inv) return;
    setDownloading(true);
    try {
      await downloadInvoicePdf(token, inv);
    } catch (e) {
      toast.error(e instanceof MemberApiError ? e.message : 'Could not download the invoice');
    } finally {
      setDownloading(false);
    }
  };

  const tax = inv?.documentType === 'tax_invoice';
  const status = inv ? STATUS[inv.status] : null;

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="dp dp-l">Invoice</div>
      </div>

      {error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : !inv || !status ? (
        <div className="col" style={{ gap: 12 }}>
          <div className="skel" style={{ height: 130, borderRadius: 'var(--r-m)' }} />
          <div className="skel" style={{ height: 120 }} />
        </div>
      ) : (
        <>
          <div className="card ink">
            <div className="between">
              <span className="lbl">{inv.title}</span>
              <span className={status.cls}>{status.label}</span>
            </div>
            <div className="dp dp-m num" style={{ marginTop: 8 }}>{hero(inv.totals.total)}</div>
            <div className="sub trunc">{inv.number ?? 'No number'} · {fmtDateShort(inv.date)}</div>
            {inv.balanceDue > 0 && inv.status !== 'cancelled' && (
              <div className="mid num" style={{ marginTop: 8, color: 'var(--lime)' }}>
                {inrExact(inv.balanceDue)} due{inv.dueDate ? ` by ${fmtDateShort(inv.dueDate)}` : ''}
              </div>
            )}
          </div>

          <div className="col" style={{ gap: 9 }}>
            <div className="lbl" style={{ paddingLeft: 2 }}>{inv.lines.length === 1 ? 'Item' : `${inv.lines.length} items`}</div>
            <div className="card" style={{ padding: '5px 16px' }}>
              {inv.lines.map((l, i) => <LineRow key={i} line={l} tax={tax} />)}
            </div>
          </div>

          <div className="card">
            <Row label="Subtotal" value={inrExact(inv.totals.subtotal)} />
            {inv.totals.discount > 0 && <Row label="Discount" value={`− ${inrExact(inv.totals.discount)}`} />}
            {tax && <Row label="Taxable value" value={inrExact(inv.totals.taxableValue)} />}
            {tax && (inv.totals.igst > 0
              ? <Row label="IGST" value={inrExact(inv.totals.igst)} />
              : <>
                  <Row label="CGST" value={inrExact(inv.totals.cgst)} />
                  <Row label="SGST" value={inrExact(inv.totals.sgst)} />
                </>)}
            <div style={{ borderTop: '1px solid var(--line)', margin: '10px 0' }} />
            <Row label="Total" value={inrExact(inv.totals.total)} strong />
            <div className="tiny wrap" style={{ marginTop: 6 }}>{inv.totals.amountInWords}</div>
          </div>

          <div className="col" style={{ gap: 9 }}>
            <div className="lbl" style={{ paddingLeft: 2 }}>Payments received</div>
            <div className="card">
              {inv.payments.length === 0 && (
                <div className="sub">{inv.status === 'cancelled' ? 'Nothing was paid.' : 'Nothing paid yet.'}</div>
              )}
              {inv.payments.map((p, i) => (
                <div key={i} className="between" style={{ padding: '8px 0', borderTop: i ? '1px solid var(--line)' : undefined }}>
                  <div className="col" style={{ gap: 2, minWidth: 0 }}>
                    <div className="mid trunc">{p.method}</div>
                    <div className="tiny trunc">{fmtDateShort(p.date)}{p.reference ? ` · ${p.reference}` : ''}</div>
                  </div>
                  <span className="mid num">{inrExact(p.amount)}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid var(--line)', margin: '10px 0' }} />
              <Row label="Paid" value={inrExact(inv.paid)} />
              <Row label="Balance due" value={inrExact(inv.balanceDue)} strong danger={inv.balanceDue > 0} />
            </div>
          </div>

          <div className="card flat col" style={{ gap: 3 }}>
            <div className="lbl">Billed by</div>
            <div className="row" style={{ gap: 9 }}>
              {inv.seller.logoUrl && (
                <img src={inv.seller.logoUrl} alt="" style={{ width: 38, height: 38, borderRadius: 'var(--r-s)', objectFit: 'cover', background: 'var(--bg-2)' }} />
              )}
              <div className="mid wrap">{inv.seller.tradeName}</div>
            </div>
            {inv.seller.legalName !== inv.seller.tradeName && <div className="sub wrap">{inv.seller.legalName}</div>}
            {inv.seller.gstin && <div className="sub num">GSTIN {inv.seller.gstin}</div>}
            {inv.seller.address && <div className="tiny wrap">{inv.seller.address}</div>}
            {inv.seller.branchAddress && inv.seller.branchAddress !== inv.seller.address && <div className="tiny wrap">Branch: {inv.seller.branchAddress}</div>}
            {(inv.seller.phone || inv.seller.email) && <div className="tiny trunc">{[inv.seller.phone, inv.seller.email].filter(Boolean).join(' · ')}</div>}
            {inv.placeOfSupply && (
              <div className="tiny">Place of supply: {inv.placeOfSupply.code}{inv.placeOfSupply.name ? ` – ${inv.placeOfSupply.name}` : ''}</div>
            )}
          </div>

          <div className="card flat col" style={{ gap: 3 }}>
            <div className="lbl">Billed to</div>
            <div className="mid wrap">{inv.buyer.legalName ?? inv.buyer.name}</div>
            {inv.buyer.legalName && <div className="sub">{inv.buyer.name}</div>}
            {inv.buyer.gstin && <div className="sub num">GSTIN {inv.buyer.gstin}</div>}
            {inv.buyer.phone && <div className="tiny">{inv.buyer.phone}</div>}
            {inv.buyer.address && <div className="tiny wrap">{inv.buyer.address}</div>}
          </div>

          <button className="btn ghost block" disabled={downloading} onClick={download}>
            <Icon n="out" s={16} c="var(--ink)" />
            {downloading ? 'Preparing PDF…' : 'Download PDF'}
          </button>
        </>
      )}
    </div>
  );
}

function LineRow({ line, tax }: { line: InvoiceLine; tax: boolean }) {
  const meta = [
    `${line.quantity} × ${inrExact(line.rate)}`,
    line.discount > 0 ? `− ${inrExact(line.discount)}` : null,
    tax ? `GST ${line.gstPercent}%` : null,
    tax && line.hsnSac ? `HSN ${line.hsnSac}` : null,
  ].filter(Boolean);
  return (
    <div className="lrow" style={{ alignItems: 'flex-start' }}>
      <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
        <div className="mid wrap">{line.description}</div>
        {line.detail && <div className="sub wrap">{line.detail}</div>}
        <div className="tiny wrap">{meta.join(' · ')}</div>
      </div>
      <span className="mid num">{inrExact(line.amount)}</span>
    </div>
  );
}

function Row({ label, value, strong, danger }: { label: string; value: string; strong?: boolean; danger?: boolean }) {
  return (
    <div className="between" style={{ padding: '3px 0' }}>
      <span className={strong ? 'mid' : 'sub'}>{label}</span>
      <span className={`num ${strong ? 'mid' : 'sub'}`} style={danger ? { color: 'var(--clay)' } : undefined}>{value}</span>
    </div>
  );
}
